import React from "react";
import {
  Box,
  Paper,
  Typography,
  Checkbox,
  FormControlLabel,
  Chip,
  Divider,
  Stack,
} from "@mui/material";

// Lets the user pick which of an order's still-outstanding samples this
// check-in sheet covers. "Still outstanding" is decided by the backend
// (sample.DateReceived is null) -- this component just renders whatever list
// it's given and reports the selection back up.
//
// In readOnly mode (updating an already-submitted sheet) the same list is shown
// without checkboxes: a submitted sheet's sample assignments are fixed, so there
// is nothing to select.
export default function SampleSelectionChecklist({
  samples,
  selectedIds = [],
  onChange,
  checkInSheetCount,
  totalSampleCount,
  remainingSampleCount,
  readOnly = false,
  sheetNumber,
}) {
  if (!samples || samples.length === 0) return null;

  const allSelected = samples.every((s) => selectedIds.includes(s.sampleRecordId));
  const someSelected = selectedIds.length > 0 && !allSelected;

  const toggleAll = (checked) => {
    onChange(checked ? samples.map((s) => s.sampleRecordId) : []);
  };

  const toggleOne = (id, checked) => {
    onChange(
      checked
        ? [...selectedIds, id]
        : selectedIds.filter((existingId) => existingId !== id)
    );
  };

  const labelFor = (s) => {
    const parts = [
      s.sampleNumber != null ? `Sample ${s.sampleNumber}` : `Sample #${s.sampleRecordId}`,
      s.labId?.trim(),
      s.sampleIdentity?.trim(),
      s.sampleDescription?.trim(),
    ];
    return parts.filter(Boolean).join(" — ");
  };

  return (
    <Paper elevation={1} sx={{ p: 2, mb: 3 }}>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        flexWrap="wrap"
        rowGap={1}
        sx={{ mb: 1 }}
      >
        <Typography variant="subtitle1" fontWeight={600}>
          {readOnly ? "Samples on This Check-In Sheet" : "Samples for This Check-In"}
        </Typography>
        <Stack direction="row" spacing={1}>
          <Chip
            size="small"
            color="primary"
            label={`Check-In Sheet #${
              readOnly ? sheetNumber : (checkInSheetCount || 0) + 1
            }`}
          />
          {!readOnly && (
            <Chip
              size="small"
              variant="outlined"
              label={`${remainingSampleCount} of ${totalSampleCount} remaining`}
            />
          )}
        </Stack>
      </Stack>

      {readOnly ? (
        <Typography variant="body2" color="text.secondary">
          These samples are fixed on this sheet and can't be changed here. To check in
          different samples, start a new check-in sheet instead.
        </Typography>
      ) : (
        <FormControlLabel
          control={
            <Checkbox
              checked={allSelected}
              indeterminate={someSelected}
              onChange={(e) => toggleAll(e.target.checked)}
            />
          }
          label={`Select all remaining (${samples.length})`}
        />
      )}

      <Divider sx={{ my: 1 }} />

      <Box sx={{ maxHeight: 260, overflowY: "auto" }}>
        {samples.map((s) =>
          readOnly ? (
            <Typography
              key={s.sampleRecordId}
              variant="body2"
              sx={{ py: 0.5, pl: 0.5 }}
            >
              {labelFor(s)}
            </Typography>
          ) : (
            <FormControlLabel
              key={s.sampleRecordId}
              control={
                <Checkbox
                  checked={selectedIds.includes(s.sampleRecordId)}
                  onChange={(e) => toggleOne(s.sampleRecordId, e.target.checked)}
                />
              }
              label={labelFor(s)}
              sx={{ display: "flex", width: "100%", ml: 0 }}
            />
          )
        )}
      </Box>
    </Paper>
  );
}
